import { useServicesContext } from "@/modules/core/services/services.context"
import type { CreateCommentInput } from "@/modules/social/gateway/socialDto"
import { socialKeys } from "@/modules/social/gateway/socialKeys"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const useCreateComment = ({
  socialPostId,
  sessionId,
  userId,
  exerciseId,
}: {
  socialPostId: string | null
  sessionId: string | null
  userId: string | null
  exerciseId: string | null
}) => {
  const { socialApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateCommentInput) => {
      if (!socialPostId) {
        if (sessionId) {
          const post = await socialApi.createSocialPost({ socialPostType: "SESSION", sessionId })
          return socialApi.createComment(post.id, input)
        }
        if (userId && exerciseId) {
          const post = await socialApi.createSocialPost({ socialPostType: "EXERCISE_COMPLETION", userId, exerciseId })
          return socialApi.createComment(post.id, input)
        }
        throw new Error("Session ID or Exercise ID is required to create a social post")
      }
      return socialApi.createComment(socialPostId, input)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: socialKeys.comments(socialPostId!) })
      void queryClient.invalidateQueries({ queryKey: socialKeys.sessionPost(socialPostId!) })
      if (userId && exerciseId) {
        void queryClient.invalidateQueries({ queryKey: socialKeys.userExercisePost(userId, exerciseId) })
      }
    },
  })
}

export const useDeleteComment = (socialPostId: string | null) => {
  const { socialApi } = useServicesContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (commentId: string) => socialApi.deleteComment(socialPostId!, commentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: socialKeys.comments(socialPostId!) })
      void queryClient.invalidateQueries({ queryKey: socialKeys.sessionPost(socialPostId!) })
    },
  })
}
